import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Shield, FileText, CheckCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TermsAcceptanceModal() {
  const { token, user, refreshUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkTermsStatus = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/terms-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.needs_acceptance) {
          setShowModal(true);
        }
      } catch (error) {
        console.error('Error checking terms status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkTermsStatus();
  }, [token]);

  const handleAccept = async () => {
    if (!acceptedTerms || !acceptedPrivacy) {
      toast.error('Please accept both Terms of Service and Privacy Policy');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/accept-terms`, {
        accept_terms: true,
        accept_privacy: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Thank you for accepting our terms');
      setShowModal(false);
      
      // Refresh user data to update terms status
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept terms');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !showModal) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-[#1a1a1a]">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[#00CCFF]/20 rounded-lg flex items-center justify-center">
              <Shield size={24} className="text-[#00CCFF]" />
            </div>
            <div>
              <h2 className="text-xl font-['Chakra_Petch'] text-white uppercase">
                Terms & Privacy Update
              </h2>
              <p className="text-[#888] font-mono text-sm">
                Please review and accept to continue
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <p className="text-[#888] font-mono text-sm">
            We've updated our Terms of Service and Privacy Policy. Please review and accept them to continue using One Voice Distribution.
          </p>

          {/* Terms of Service */}
          <div className="bg-[#111] border border-[#222] rounded-lg p-4">
            <div className="flex items-start gap-3">
              <button
                onClick={() => setAcceptedTerms(!acceptedTerms)}
                className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                  acceptedTerms 
                    ? 'bg-[#00CCFF] border-[#00CCFF]' 
                    : 'border-[#444] hover:border-[#666]'
                }`}
                data-testid="accept-terms-checkbox"
              >
                {acceptedTerms && <CheckCircle size={14} className="text-black" />}
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-[#00CCFF]" />
                  <span className="text-white font-mono text-sm">Terms of Service</span>
                </div>
                <p className="text-[#666] font-mono text-xs mt-1">
                  By checking this box, I agree to the One Voice Distribution Terms of Service
                </p>
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#00CCFF] font-mono text-xs mt-2 hover:underline"
                >
                  Read Terms <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </div>

          {/* Privacy Policy */}
          <div className="bg-[#111] border border-[#222] rounded-lg p-4">
            <div className="flex items-start gap-3">
              <button
                onClick={() => setAcceptedPrivacy(!acceptedPrivacy)}
                className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                  acceptedPrivacy 
                    ? 'bg-[#00CCFF] border-[#00CCFF]' 
                    : 'border-[#444] hover:border-[#666]'
                }`}
                data-testid="accept-privacy-checkbox"
              >
                {acceptedPrivacy && <CheckCircle size={14} className="text-black" />}
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Shield size={16} className="text-[#00CCFF]" />
                  <span className="text-white font-mono text-sm">Privacy Policy</span>
                </div>
                <p className="text-[#666] font-mono text-xs mt-1">
                  By checking this box, I agree to the One Voice Distribution Privacy Policy
                </p>
                <a 
                  href="/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#00CCFF] font-mono text-xs mt-2 hover:underline"
                >
                  Read Privacy Policy <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#1a1a1a]">
          <Button
            onClick={handleAccept}
            disabled={!acceptedTerms || !acceptedPrivacy || submitting}
            className="w-full bg-[#00CCFF] hover:bg-[#00b8e6] text-black font-['Chakra_Petch'] uppercase disabled:opacity-50"
            data-testid="accept-terms-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Processing...
              </span>
            ) : (
              'Accept & Continue'
            )}
          </Button>
          <p className="text-[#555] font-mono text-xs text-center mt-3">
            You must accept both to use the platform
          </p>
        </div>
      </div>
    </div>
  );
}
